import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"
import { formatInTimeZone } from "date-fns-tz"

/**
 * True when the given date falls on "today" in German time (Europe/Berlin),
 * i.e. after the most recent midnight in Berlin — independent of the
 * viewer's local timezone.
 */
export function isTodayInBerlin(date: Date): boolean {
  if (isNaN(date.getTime())) return false;
  const day = 'yyyy-MM-dd';
  return formatInTimeZone(date, 'Europe/Berlin', day) === formatInTimeZone(new Date(), 'Europe/Berlin', day);
}

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function isWithin12Hours(date: Date, compareDate: Date): boolean {
  const twelveHoursInMs = 12 * 60 * 60 * 1000;
  return Math.abs(compareDate.getTime() - date.getTime()) < twelveHoursInMs;
}

// ==========================================
// CLIENT DATA VALIDATION & HANDLING UTILITIES
// ==========================================

export interface ClientDataValidation {
  isValid: boolean;
  warnings: string[];
  errors: string[];
  hasValidEmails: boolean;
  validEmailCount: number;
}

/**
 * Comprehensive email validation function
 */
export function isValidEmail(email: string): boolean {
  if (!email || typeof email !== 'string') return false;
  
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const trimmedEmail = email.trim();
  
  // Basic format check
  if (!emailRegex.test(trimmedEmail)) return false;
  
  // Additional checks for common issues
  if (trimmedEmail.length > 254) return false; // RFC 5321 limit
  if (trimmedEmail.includes('..')) return false; // Double dots not allowed
  if (trimmedEmail.startsWith('.') || trimmedEmail.endsWith('.')) return false;
  
  return true;
}

/**
 * Validates complete client data and returns detailed validation results
 */
export function validateClientData(client: any): ClientDataValidation {
  const warnings: string[] = [];
  const errors: string[] = [];
  let hasValidEmails = false;
  let validEmailCount = 0;

  // Company name validation
  if (!client?.companyName?.trim()) {
    warnings.push("Missing company name - will display as 'Client XX' in interfaces");
  }

  // Primary email validation
  if (!client?.clientEmailMain?.trim()) {
    errors.push("No primary email address configured");
  } else if (!isValidEmail(client.clientEmailMain)) {
    errors.push("Primary email address is invalid");
  } else {
    hasValidEmails = true;
    validEmailCount++;
  }

  // Secondary emails validation
  if (client?.clientEmailSecondaries && Array.isArray(client.clientEmailSecondaries)) {
    const validSecondaries = client.clientEmailSecondaries.filter((email: string) => 
      email && email.trim() && isValidEmail(email.trim())
    );
    
    const invalidSecondaries = client.clientEmailSecondaries.filter((email: string) => 
      email && email.trim() && !isValidEmail(email.trim())
    );

    validEmailCount += validSecondaries.length;
    
    if (validSecondaries.length > 0) {
      hasValidEmails = true;
    }

    if (invalidSecondaries.length > 0) {
      warnings.push(`${invalidSecondaries.length} invalid secondary email${invalidSecondaries.length !== 1 ? 's' : ''}`);
    }
  }

  // Phone validation
  if (!client?.phone?.trim()) {
    warnings.push("No phone number provided");
  }

  // Address validation
  if (!client?.address?.trim()) {
    warnings.push("No address information");
  }

  return {
    isValid: errors.length === 0,
    warnings,
    errors,
    hasValidEmails,
    validEmailCount
  };
}

/**
 * Gets safe display name for client, handling empty/missing company names
 */
export function getClientDisplayName(client: any): string {
  if (!client) return "Unknown Client";
  
  const companyName = client.companyName?.trim();
  const clientId = client.id || "??";
  
  if (!companyName) {
    return `${clientId} - [No Company Name]`;
  }
  
  return `${clientId} - ${companyName}`;
}

/**
 * Gets fallback display name when company name is missing
 */
export function getClientFallbackName(client: any): string {
  if (!client) return "Unknown Client";
  
  const companyName = client.companyName?.trim();
  if (companyName) return companyName;
  
  return `Client ${client.id || "??"}`;
}

/**
 * Extracts valid email addresses from client data
 */
export function getValidClientEmails(client: any): Array<{email: string, name: string, isPrimary: boolean}> {
  const emails: Array<{email: string, name: string, isPrimary: boolean}> = [];
  const fallbackName = getClientFallbackName(client);

  // Add primary email if valid
  if (client?.clientEmailMain?.trim() && isValidEmail(client.clientEmailMain)) {
    emails.push({
      email: client.clientEmailMain.trim(),
      name: fallbackName,
      isPrimary: true
    });
  }

  // Add valid secondary emails
  if (client?.clientEmailSecondaries && Array.isArray(client.clientEmailSecondaries)) {
    client.clientEmailSecondaries.forEach((email: string) => {
      if (email && email.trim() && isValidEmail(email.trim())) {
        emails.push({
          email: email.trim(),
          name: fallbackName,
          isPrimary: false
        });
      }
    });
  }

  return emails;
}

/**
 * Generates user-friendly validation message for client data issues
 */
export function getClientValidationMessage(validation: ClientDataValidation): string {
  if (validation.isValid && validation.warnings.length === 0) {
    return "Client data is complete and valid";
  }

  const messages: string[] = [];

  if (validation.errors.length > 0) {
    messages.push(`${validation.errors.length} error${validation.errors.length !== 1 ? 's' : ''}: ${validation.errors.join(', ')}`);
  }

  if (validation.warnings.length > 0) {
    messages.push(`${validation.warnings.length} warning${validation.warnings.length !== 1 ? 's' : ''}: ${validation.warnings.join(', ')}`);
  }

  return messages.join(' | ');
}

/**
 * Checks if client has sufficient data for email functionality
 */
export function canClientReceiveEmails(client: any): { canReceive: boolean; reason?: string } {
  const validation = validateClientData(client);
  
  if (!validation.hasValidEmails) {
    return {
      canReceive: false,
      reason: validation.validEmailCount === 0 
        ? "No valid email addresses configured"
        : "Primary email address is invalid"
    };
  }

  return { canReceive: true };
}

/**
 * Converts URLs in text to clickable anchor tags that open in new tabs.
 * Handles http, https, and www. prefixed URLs.
 */
export function linkifyText(text: string): string {
  if (!text) return text;
  
  // Regex to match URLs (http, https, or www.)
  const urlRegex = /(\b(?:https?:\/\/|www\.)[^\s<>"']+)/gi;
  
  return text.replace(urlRegex, (url) => {
    // Add protocol if missing (for www. URLs)
    const href = url.startsWith('www.') ? `https://${url}` : url;
    // Escape HTML entities in URL for safety
    const escapedHref = href.replace(/"/g, '&quot;');
    const escapedUrl = url.replace(/</g, '&lt;').replace(/>/g, '&gt;');
    return `<a href="${escapedHref}" target="_blank" rel="noopener noreferrer" class="text-blue-400 hover:text-blue-300 underline">${escapedUrl}</a>`;
  });
}

/**
 * Converts URLs in HTML content to clickable anchor tags, without touching
 * URLs that are already inside <a> tags or HTML attributes.
 * Splits the HTML by tags, only processes text segments.
 */
export function linkifyHtml(html: string): string {
  if (!html) return html;

  const urlRegex = /(\b(?:https?:\/\/|www\.)[^\s<>"']+)/gi;

  const parts = html.split(/(<[^>]*>)/);
  let insideAnchor = 0;

  return parts.map((part) => {
    if (part.startsWith('<')) {
      const tagLower = part.toLowerCase();
      if (tagLower.startsWith('<a ') || tagLower === '<a>') insideAnchor++;
      else if (tagLower.startsWith('</a')) insideAnchor = Math.max(0, insideAnchor - 1);
      return part;
    }
    if (insideAnchor > 0) return part;

    return part.replace(urlRegex, (url) => {
      const href = url.startsWith('www.') ? `https://${url}` : url;
      const escapedHref = href.replace(/"/g, '&quot;');
      const escapedUrl = url.replace(/</g, '&lt;').replace(/>/g, '&gt;');
      return `<a href="${escapedHref}" target="_blank" rel="noopener noreferrer" class="text-blue-400 hover:text-blue-300 underline">${escapedUrl}</a>`;
    });
  }).join('');
}

/**
 * Map of shortCode to firstName for @mention display
 */
export type MentionUserMap = Map<string, string>;

/**
 * Replaces @shortCode mentions with @firstName and converts URLs to links.
 * Used in chat messages to display readable names instead of codes.
 */
export function renderMessageContent(text: string, userMap?: MentionUserMap): string {
  if (!text) return text;
  
  let result = text;
  
  // First, replace @mentions with firstName if userMap is provided
  if (userMap && userMap.size > 0) {
    // Match @followed by word characters (the shortCode)
    const mentionRegex = /@(\w+)/g;
    result = result.replace(mentionRegex, (match, shortCode) => {
      const firstName = userMap.get(shortCode.toUpperCase()) || userMap.get(shortCode);
      if (firstName) {
        return `<span class="text-blue-400 font-medium">@${firstName}</span>`;
      }
      return match; // Keep original if no match found
    });
  }
  
  // Then linkify URLs
  return linkifyText(result);
}
